<template>
  <div class="w-full flex flex-col border border-border-normal" style="border-radius: 18px;">
    <!-- Header -->
    <div class="w-full flex items-center p-3">
      <!-- Title -->
      <div class="flex items-center gap-1 p-1">
        <ScatterGraphIcon class="text-icon-muted" />
        <BaseText type="label" size="sm" class="text-text-muted">
          Top 100
        </BaseText>
      </div>
      <!-- Info Label -->
      <button class="group ml-1 flex items-center p-1"
      @click="$emit('show-info-modal')">
        <InfoIcon class="text-text-subdued transition-colors duration-100 group-hover:text-text-normal" />
        <BaseText type="label" size="sm" class="text-text-normal px-1 transition-colors duration-100 group-hover:text-text-muted">
          How is rank determined?
        </BaseText>
      </button>
      <!-- Date Selector -->
      <div class="relative ml-auto">
        <button class="flex items-center gap-1 px-1.5 py-1 rounded-md transition-colors duration-100"
        :class="showDateSelector ? 'bg-neutral-25' : 'hover:bg-neutral-25'"
        @click="showDateSelector = !showDateSelector">
          <CalendarIcon class="text-icon-normal" />
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ dateFilterLabel }}
          </BaseText>
        </button>
        <transition>
          <div v-if="showDateSelector" class="date-selector-dropdown" v-on-clickaway="() => showDateSelector = false">
            <DateRangeSelectorDropdown
              single-date
              :selected-date.sync="selectedDate"
              close-on-apply
              info-label="The chart will show ad rankings at this date"
              @close="showDateSelector = false"
            />
          </div>
        </transition>
      </div>
    </div>
    <!-- Graph Area -->
    <div class="w-full mb-3" style="height: 332px;">
      <AdRankScatterChart
        :scatter-data="scatterData"
        :loading="loading"
        @show-ad-details="showAdvertisementDetails"
      />
    </div>
    <!-- Advertisement Details Drawer -->
    <AdvertisementDetailsDrawer
      v-if="showAdvertisementDetailsDrawer"
      :advertisement="selectedAdvertisement"
      discovery
      @close="showAdvertisementDetailsDrawer = false"
    />
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import moment from 'moment'
import { mixin as clickaway } from 'vue-clickaway2'

// Components
import AdRankScatterChart from './AdRankScatterChart.vue'
import AdvertisementDetailsDrawer from '@/components/advertisements/AdvertisementDetailsDrawer.vue'
import DateRangeSelectorDropdown from '@/components/search/TopBarComponents/DateRangeSelectorDropdown.vue'

// Icons
import ScatterGraphIcon from '@/components/globals/Icons/LensIcons/graphs/ScatterGraphIcon.vue'
import InfoIcon from '@/components/globals/Icons/InfoSimpleIcon.vue'
import CalendarIcon from '@/components/globals/Icons/CalendarIcon.vue'

export default {
  name: 'AdRankingGraph',
  mixins: [clickaway],
  components: {
    AdRankScatterChart,
    AdvertisementDetailsDrawer,
    DateRangeSelectorDropdown,
    ScatterGraphIcon,
    InfoIcon,
    CalendarIcon
  },
  props: {
    pageId: {
      required: true
    }
  },
  data () {
    return {
      scatterData: {},
      loading: true,
      showDateSelector: false,
      selectedDate: null,
      showAdvertisementDetailsDrawer: false,
      selectedAdvertisement: null,
    }
  },
  watch: {
    pageId: {
      handler(pageId) {
        if (!pageId) return
        this.fetchScatterData()
      },
      immediate: true
    },
    selectedDate () {
      this.fetchScatterData()
    }
  },
  computed: {
    dateFilterLabel () {
      if (!this.selectedDate) return moment().format('MMMM Do, YYYY')
      return moment(this.selectedDate).format('MMMM Do, YYYY')
    }
  },
  methods: {
    async fetchScatterData () {
      if (!this.pageId) return
      this.loading = true
      try {
        const date = this.selectedDate ? moment(this.selectedDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
        const { data } = await ForeplayAPI.Brands.getBrandRankingsScatterData(this.pageId, date, 7)
        this.scatterData = data
      } catch (error) {
        console.error('Error fetching scatter data:', error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch scatter data. See console for details.'
        })
      } finally {
        this.loading = false
      }
    },
    showAdvertisementDetails (advertisement) {
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    }
  }
}
</script>

<style scoped>
.date-selector-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 500;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>